import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from "lucide-react";
import { toast } from 'sonner';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'ConnectButton' });

export const ConnectButton = () => {
  const navigate = useNavigate();
  const { isInitialized, user, profile, isLoading, authError } = useAuth();
  const [isClicked, setIsClicked] = useState(false);
  const [buttonText, setButtonText] = useState<string>("");
  const [isConnecting, setIsConnecting] = useState(false);
  
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isClicked) {
      timeout = setTimeout(() => {
        setIsClicked(false);
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [isClicked]);

  useEffect(() => {
    let text = "Login";
    
    if (!isInitialized) {
      text = "Initializing...";
    } else if (isLoading) {
      text = "Loading...";
    } else if (authError) {
      text = "Login Error";
    } else if (user) {
      if (profile && profile.username && !profile.username.startsWith('user_')) {
        text = `@${profile.username}`;
      } else {
        text = "Go to App";
      }
    }
    
    setButtonText(text);
  }, [isInitialized, isLoading, authError, user, profile]);
  
  const handleNavigateToAuth = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    log.info('ConnectButton - handleNavigateToAuth called');
    
    setIsClicked(true);
    setIsConnecting(true);
    
    try {
      if (!isInitialized) {
        log.info('Auth not initialized');
        toast.error("Still initializing, please wait a moment");
        setIsConnecting(false);
        return;
      }
      
      if (user) {
        log.info('User already logged in, navigating to home');
        navigate('/', { replace: true });
        setIsConnecting(false);
        return;
      }
      
      log.info('Navigating to auth page');
      navigate('/auth', { replace: true });
    } catch (error) {
      log.error('Error in handleNavigateToAuth:', error);
      toast.error("Login error. Please try again.");
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Button 
      onClick={handleNavigateToAuth}
      className="relative min-w-[140px]"
      disabled={!isInitialized || (isLoading && !isClicked) || isConnecting}
      variant={authError ? "destructive" : "default"}
    >
      {(isLoading || !isInitialized || isConnecting) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {buttonText}
    </Button>
  );
};
